import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { MoveLeft, ChevronDown, Loader } from "lucide-react";
import tata from "../../../assets/Vehicles/tata.png";
import tool from "../../../assets/Vehicles/tool.png";
import coin from "../../../assets/Vehicles/coin.png";
import petrol from "../../../assets/Vehicles/petrol.png";
import Dropdown from "react-bootstrap/Dropdown";
import { Button, Card, Toast, ToastContainer, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import "../Vehicle_list/List.css";

import { Chart as ChartJS, LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Filler, ArcElement } from "chart.js";
import { getCookie } from "../../getcookie.js";
import Loading from "../../Loading.jsx";
import attachment from "../../../assets/png/attachment.png";
import download from "../../../assets/svg/download.svg";
import { ServiceHistoryTableList } from "./_components/ServiceHistoryTableList.jsx";
import { FuelHistoryTableList } from "./_components/FuelHistoryTableList.jsx";
import { KMHistoryTableList } from "./_components/KMHistoryTableList.jsx";
import { RenewalHistoryTableList } from "./_components/RenewalHistoryTableList.jsx";
import { IssueHistoryTableList } from "./_components/IssueHistoryTableList.jsx";
import { ServiceReminderTableList } from "./_components/ServiceReminderTableList.jsx";
import { InspectionHistoryTableList } from "./_components/InspectionHistoryTableList.jsx";
import { ComplaintsTableList } from "./_components/ComplaintsTableList.jsx";
import { AssignedTableList } from "./_components/AssignedTableList.jsx";
import { Specifications } from "./_components/Specification.jsx";
import { FinancialView } from "./_components/FinancialView.jsx";
import { useGlobal } from "../../../GlobalContext.js";
import right from "../../../assets/svg/right.svg";
import left from "../../../assets/svg/left.svg";
import OpenIcon from "../../../assets/svg/open_ic.svg";
import OverDueIcon from "../../../assets/svg/overdue_ic.svg";
import VehicleDocumentsPage from "./_components/vehicle-document-page.jsx";
import { getEparivahanAPI, refreshVehicleHistoryAPI } from "./vehicle_api";

// Register ChartJS elements globally
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip, Filler, ArcElement);

// --- Constants ---

const SUB_SIDEBAR = ["Overview", "Financial", "Specifications", "Service history", "Fuel history", "KM history", "Renewal reminder", "Issues", "Service reminder", "Inspection history", "Complaint", "Assigned history"];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const API_ENDPOINTS = {
  Issues: `issue/list_by_vehicle/`,
  "Service Reminder": `service_reminder/list_by_vehicle/`,
  "Renewal Reminder": `renewal_reminder/list_by_vehicle/`,
  Inspections: `inspection/list_by_vehicle/`,
};

// --- Helper Functions ---

const getHeader = () => ({
  "Content-Type": "application/json; charset=UTF-8",
  Accept: "application/json",
  "Access-Control-Allow-Origin": "*",
  Authorization: getCookie("mawalog"),
});

const downloadFile = (fileUrl, fileName) => {
  fetch(fileUrl)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => console.error("Download error:", error));
};

const downloadSvgFile = async (url, lic_no) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `${lic_no || "download"}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export const VehicleDetails = () => {
  const { rolePermissionList } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};
  const apiUrl = process.env.REACT_APP_API_URL;

  // State
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(null);

  const [activeTab, setActiveTab] = useState("Overview");
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  const [expenseLoading, setExpenseLoading] = useState(true);
  const [selected, setSelected] = useState("Monthly");
  const [period, setPeriod] = useState("monthly");
  const [serviceExpense, setServiceExpense] = useState([]);
  const [serviceTotal, setServiceTotal] = useState("--");
  const [fuelTotal, setFuelTotal] = useState("--");
  // eslint-disable-next-line
  const [fuelExpense, setFuelExpense] = useState([]);
  const [otherTotal, setOtherTotal] = useState("--");
  // eslint-disable-next-line
  const [otherExpense, setOtherExpense] = useState([]);

  const [activeButton] = useState("Issues");
  const [tabData, setTabData] = useState(null);

  const [isParivahan, setIsParivahan] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [docRefreshKey, setDocRefreshKey] = useState(0);

  const scrollRef = useRef(null);

  // --- Callbacks ---

  const handleSnackbarShow = useCallback(() => setShowSnackbar(true), []);
  const handleSnackbarClose = useCallback(() => setShowSnackbar(false), []);

  const handleClose = useCallback(
    (response) => {
      if (response?.data) {
        setSnackbarMessage(response.data.message);
        setSnackbarStatus(response.data.success);
        handleSnackbarShow();
      }
    },
    [handleSnackbarShow],
  );

  const handleScroll = useCallback((dir) => {
    const scrollAmount = 200;
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  }, []);

  const handleSelect = useCallback((value) => {
    setSelected(value === "monthly" ? "Monthly" : "Yearly");
    setPeriod(value);
  }, []);

  const checkPermission = useCallback(
    (menuName, permission) => {
      return rolePermissionList.findIndex((element) => element["name"] === menuName && element["menu_permission"] === true && element[permission] === true) > -1;
    },
    [rolePermissionList],
  );

  // --- Effects ---

  // Fetch Vehicle Details
  const fetchVehicle = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}vehicle/view/${id}`, {
        headers: getHeader(),
      });
      if (response.data?.success) {
        setVehicle(response.data.vehicle);
      }
    } catch (err) {
      console.error("Error fetching vehicle:", err);
    } finally {
      setLoading(false);
    }
  }, [id, apiUrl]);

  useEffect(() => {
    if (id) fetchVehicle();
  }, [id, fetchVehicle]);

  // Fetch Expenses
  useEffect(() => {
    const fetchExpenses = async () => {
      setExpenseLoading(true);
      try {
        const response = await axios.get(`${apiUrl}vehicle/vehicle_expense_details/${id}`, {
          headers: getHeader(),
          params: { period: period },
        });

        if (response?.data?.success) {
          const data = response.data;
          setServiceExpense(data.serviceExpense || []);
          setServiceTotal(data.serviceTotalExpense?.sum || "--");

          setFuelExpense(data.vehicleFuelExpense || []);
          setFuelTotal(data.fuelTotalExpense?.sum || "--");

          setOtherExpense(data.vehicleOtherExpense || []);
          setOtherTotal(data.otherExpense?.sum || "--");
        }
      } catch (err) {
        console.error("Error fetching expense:", err);
      } finally {
        setExpenseLoading(false);
      }
    };
    if (id) fetchExpenses();
  }, [id, period, apiUrl]);

  // Fetch Tab Data (Issues, etc.)
  useEffect(() => {
    const fetchTabData = async () => {
      try {
        const response = await axios.get(`${apiUrl}${API_ENDPOINTS[activeButton]}${id}`, {
          headers: getHeader(),
        });
        setTabData(response.data.data);
      } catch (err) {
        console.error("Error fetching tab data:", err);
      }
    };
    if (id && activeButton) fetchTabData();
  }, [id, activeButton, apiUrl]);

  // Fetch Parivahan Integration Status
  useEffect(() => {
    const fetchParivahanStatus = async () => {
      try {
        const response = await getEparivahanAPI();
        if (response.data?.success) {
          setIsParivahan(response.data.is_true ? true : false);
        }
      } catch (err) {
        console.error("Error fetching Parivahan status:", err);
      }
    };
    fetchParivahanStatus();
  }, []);

  // --- Memoized Values ---

  const { chartData, chartOptions } = useMemo(() => {
    const expenseMap = {};
    serviceExpense.forEach((item) => {
      expenseMap[item.period] = Number(item.total);
    });

    const monthlyData = MONTHS.map((month) => expenseMap[month] || 0);
    const years = serviceExpense.map((item) => item.period);
    const yearlyData = years.map((year) => expenseMap[year] || 0);

    const data = {
      labels: selected === "Monthly" ? MONTHS : years,
      datasets: [
        {
          label: selected === "Monthly" ? "Monthly Data" : "Yearly Data",
          data: selected === "Monthly" ? monthlyData : yearlyData,
          borderColor: "#9C27B0",
          fill: false,
          tension: 0.5,
          pointRadius: 0,
          pointHoverRadius: 8,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (context) {
              const val = Number(context.formattedValue);
              return `₹ ${isNaN(val) ? context.formattedValue : val.toFixed(2)}`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          max: serviceTotal,
          ticks: {
            stepSize: 50,
            color: "#999",
            callback: function (value) {
              const num = Number(value);
              return isNaN(num) ? value : num.toFixed(2);
            },
          },
          grid: {
            display: true,
          },
        },
        x: {
          ticks: {
            color: "#999",
          },
          grid: {
            display: false,
          },
        },
      },
    };

    return { chartData: data, chartOptions: options };
  }, [serviceExpense, selected, serviceTotal]); // valid dependencies

  // --- Render ---

  return (
    <>
      <div className="d-flex flex-column flex-xl-row">
        <div style={{ width: "calc(100vw - 251px)" }} className="bg-half-white">
          <div className="w-100 d-flex flex-wrap justify-content-between align-items-center bg-white p-3 m-0">
            <div className="d-flex">
              <div className="d-flex align-items-center gap-3">
                <div className="border p-2 rounded-circle cursor-pointer" onClick={() => navigate(-1)}>
                  <MoveLeft />
                </div>
                <div className="bg-half-white rounded-4">
                  <img src={tata} alt="tata" />
                </div>
                <div>
                  <p className="m-0 fs-20 fw-semibold mb-1">{vehicle?.lic_no ?? "XX XXX XXX"}</p>
                  <div className="d-flex align-items-center">
                    <span className="me-2 text-muted-grey">{vehicle?.model ?? "--"}</span>
                    <span className="me-2">•</span>
                    <span className="border-0 bg-list-purple text-dark-purple py-1 px-4 rounded-pill">{vehicle?.year_of_make ?? "----"}</span>
                  </div>
                </div>
              </div>
              {vehicle && vehicle.qr && vehicle.qr !== "" && (
                <div style={{ padding: "0 10px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <img
                      src={vehicle.qr}
                      alt="QR Code"
                      style={{
                        width: "80px",
                        height: "80px",
                        objectFit: "cover",
                      }}
                    />
                    <div style={{ padding: "0 10px" }}>
                      <button
                        onClick={() => downloadSvgFile(vehicle.qr || "", vehicle.lic_no || "")}
                        style={{
                          width: "36px",
                          height: "36px",
                          backgroundColor: "#471070",
                          border: "2px solid #471070",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={download}
                          alt="Download"
                          style={{
                            width: "15px",
                            height: "15px",
                            filter: "invert(1)",
                            fill: "white",
                          }}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-3">
              {checkPermission("Vehicle", "edit_permission") && (
                <Button
                  onClick={() => {
                    navigate("/vehicle-create", {
                      state: { id: location.state?.id },
                    });
                  }}
                  className="dark-purple-btn rounded-pill py-2 px-4"
                >
                  Edit
                </Button>
              )}
            </div>
          </div>
          <div className="w-100">
            <div
              className="d-flex align-items-center rounded-4"
              style={{
                background: "#F5F7F9",
                borderBottom: "1px solid #EBEBEB",
                boxShadow: "0px 4px 6px 0px #0000001A",
              }}
            >
              <button type="button" className="border-0 p-2" onClick={() => handleScroll("left")}>
                <img src={left} alt="left" />
              </button>
              <div ref={scrollRef} className="w-100 scroll-hidden d-flex flex-row align-items-center gap-3 overflow-auto" style={{ whiteSpace: "nowrap" }}>
                {(() => {
                  const tabs = [...SUB_SIDEBAR];
                  if (isParivahan) {
                    tabs.splice(1, 0, "RC, Insurance & Permit details");
                  }
                  return tabs.map((value) => (
                    <Button
                      key={value}
                      className={activeTab === value ? "border-top-0 border-end-0 border-start-0  rounded-0 bg-transparent w-100 text-dark-purple d-flex  subSidebar-btn fw-500 fs-14" : "border-0 bg-transparent text-grey w-100 d-flex subSidebar-btn fw-400 fs-14"}
                      style={{
                        borderBottom: activeTab === value ? "2px solid #471070" : "",
                      }}
                      onClick={() => setActiveTab(value)}
                    >
                      {value}
                    </Button>
                  ));
                })()}
              </div>

              <button type="button" className="border-0 p-2" onClick={() => handleScroll("right")}>
                <img src={right} alt="right" />
              </button>
            </div>
          </div>
          <div className="row m-0">
            <div className="col-sm-12">
              <div className="d-flex flex-wrap gap-3 overviewGraph mt-3">
                {activeTab === "Overview" && (
                  <>
                    <div className="col-xl-7 col-12 m-0 p-0">
                      <div className="bg-white rounded-4 p-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <p className="m-0 fw-600">Cost of ownership</p>
                          <Dropdown>
                            <Dropdown.Toggle variant="success" id="dropdown-basic" className=" bg-white border rounded-3 d-flex justify-content-between align-items-center gap-2 dpm-toggle">
                              <span>{selected}</span>
                              <span className="pagination-color">
                                <ChevronDown />
                              </span>
                            </Dropdown.Toggle>

                            <Dropdown.Menu style={{ minWidth: "unset", width: "100%" }}>
                              <Dropdown.Item onClick={() => handleSelect("monthly")}>Monthly</Dropdown.Item>
                              <Dropdown.Item onClick={() => handleSelect("yearly")}>Yearly</Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                        {expenseLoading ? (
                          <Loading />
                        ) : (
                          <div className="row mt-3 gap-3 gap-xl-0">
                            <div className="col-xl-4">
                              <div className="serviceCard p-3 rounded-4">
                                <div className="d-flex flex-wrap gap-3 align-items-center justify-content-center">
                                  <img src={tool} alt="tool" />
                                  <div className="d-flex flex-column">
                                    <p className="m-0 .service-text-color">Service cost</p>
                                    <p className="m-0">₹ {serviceTotal}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-4">
                              <div className="costCard p-3 rounded-4">
                                <div className="d-flex flex-wrap gap-3 align-items-center justify-content-center">
                                  <img src={coin} alt="coin" />
                                  <div className="d-flex flex-column">
                                    <p className="m-0 cost-text-color">Other cost</p>
                                    <p className="m-0">₹ {otherTotal}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-xl-4">
                              <div className="fuelCard p-3 rounded-4">
                                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-center">
                                  <img src={petrol} alt="petrol" />
                                  <div className="d-flex flex-column">
                                    <p className="m-0 fuel-text-color">Fuel cost</p>
                                    <p className="m-0">₹ {fuelTotal}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <h2 className="my-3">Service Expenses</h2>
                            <Line data={chartData} options={chartOptions} height={100} />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col bg-white rounded-4">
                      <div className="bg-white rounded-4 p-3 pt-3">
                        <p className="fs-16 fw-600 m-0">Details</p>
                        <div className=" rounded-4 overflow-hidden my-3 ">
                          {loading ? (
                            <Loading />
                          ) : (
                            <table
                              className=" mb-0 no-border"
                              style={{
                                border: "none",
                                borderCollapse: "collapse",
                              }}
                            >
                              <tbody>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    Name
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.vehicle_name ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    Kilometer
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.km}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    Group
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.group ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    Operation
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.operation ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    VIN/SIN
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.vin ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    License Plate
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.lic_no ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    Modal
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.model ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    Trim
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.trim ?? "-"}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="fs-13 fw-300" style={{ color: "#49494B" }}>
                                    OwnerShip
                                  </td>
                                  <td className="fs-13_5 fw-500" style={{ color: "#172B4D" }}>
                                    {vehicle?.ownership ?? "-"}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex flex-row  gap-3 w-100">
                      <div className="bg-white rounded-4 p-3" style={{ height: "254px", width: "25%" }}>
                        <div className="d-flex justify-content-between">
                          <div className="fs-13 fw-600 textColor">ISSUES : {tabData?.list?.length ?? 0}</div>
                        </div>
                        <div className="d-flex gap-3 mt-2">
                          <div
                            className="p-1 border-0"
                            style={{
                              backgroundColor: "#FFF0F0",
                              borderRadius: "10px",
                              width: "50%",
                              height: "36px",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center px-2">
                              <div className="d-flex gap-1 align-items-center">
                                <img src={OverDueIcon} alt="due" />
                                <p className="m-0 ">Closed</p>
                              </div>
                              <p className="m-0" style={{ color: "#EB5F5E" }}>
                                {tabData?.closed ?? 0}
                              </p>
                            </div>
                          </div>
                          <div
                            className="p-1 border-0"
                            style={{
                              backgroundColor: "#EFFFFA",
                              borderRadius: "10px",
                              width: "50%",
                              height: "36px",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center px-2">
                              <div className="d-flex gap-1 align-items-center">
                                <img src={OpenIcon} alt="due" />
                                <p className="m-0">Open</p>
                              </div>
                              <p className="m-0" style={{ color: "#61C1A8" }}>
                                {tabData?.closed ?? 0}
                              </p>
                            </div>
                          </div>
                        </div>
                        {tabData?.list?.length > 0 ? (
                          tabData.list.map((issue, index) => (
                            <div className="issue-card mt-1" key={index}>
                              <div className="issue-header">
                                <span className="issue-id">#{issue.name}</span>
                                <span className="issue-time"> {issue.time_ago}</span>
                              </div>
                              <div className="issue-description">{issue.description}</div>
                            </div>
                          ))
                        ) : (
                          <li className="list-group-item text-center text-muted mt-2">No issues found</li>
                        )}
                      </div>
                      <div className="bg-white rounded-4 p-3" style={{ height: "254px", width: "25%" }}>
                        <div className="d-flex justify-content-between">
                          <div className="fs-13 fw-600 textColor">RENEWAL REMINDER : {tabData?.list?.length ?? 0}</div>
                        </div>
                        <div className="d-flex gap-3 mt-2">
                          <div
                            className="p-1 border-0"
                            style={{
                              backgroundColor: "#FFF0F0",
                              borderRadius: "10px",
                              width: "50%",
                              height: "36px",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center px-2">
                              <div className="d-flex gap-1 align-items-center">
                                <img src={OverDueIcon} alt="due" />
                                <p className="m-0">Closed</p>
                              </div>
                              <p className="m-0" style={{ color: "#EB5F5E" }}>
                                {tabData?.closed ?? 0}
                              </p>
                            </div>
                          </div>
                          <div
                            className="p-1 border-0"
                            style={{
                              backgroundColor: "#EFFFFA",
                              borderRadius: "10px",
                              width: "50%",
                              height: "36px",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center px-2">
                              <div className="d-flex gap-1 align-items-center">
                                <img src={OpenIcon} alt="due" />
                                <p className="m-0">Open</p>
                              </div>
                              <p className="m-0" style={{ color: "#61C1A8" }}>
                                {tabData?.closed ?? 0}
                              </p>
                            </div>
                          </div>
                        </div>
                        {tabData?.list?.length > 0 ? (
                          tabData.list.map((issue, index) => (
                            <div className="issue-card mt-1" key={index}>
                              <div className="issue-description">{issue.description}</div> <span className="issue-time"> {issue.time_ago}</span>
                            </div>
                          ))
                        ) : (
                          <li className="list-group-item text-center text-muted mt-2">No issues found</li>
                        )}
                      </div>
                      <div className="bg-white rounded-4 p-3" style={{ height: "254px", width: "25%" }}>
                        <div className="d-flex justify-content-between">
                          <div className="fs-13 fw-600 textColor">REMINDER : {tabData?.list?.length ?? 0}</div>
                        </div>
                        {tabData?.list?.length > 0 ? (
                          tabData.list.map((issue, index) => (
                            <div className="issue-card mt-1" key={index}>
                              <div className="issue-description">{issue.description}</div> <span className="issue-time"> {issue.time_ago}</span>
                            </div>
                          ))
                        ) : (
                          <li className="list-group-item text-center text-muted mt-2">No issues found</li>
                        )}
                      </div>
                      <div className="bg-white rounded-4 p-3" style={{ height: "254px", width: "25%" }}>
                        <div className="d-flex justify-content-between">
                          <div className="fs-13 fw-600 textColor">INSPECTIONS : {tabData?.list?.length ?? 0}</div>
                        </div>
                        {tabData?.list?.length > 0 ? (
                          tabData.list.map((issue, index) => (
                            <div className="issue-card mt-1" key={index}>
                              <div className="issue-description">{issue.description}</div> <span className="issue-time"> {issue.time_ago}</span>
                            </div>
                          ))
                        ) : (
                          <li className="list-group-item text-center text-muted mt-2">No issues found</li>
                        )}
                      </div>
                    </div>

                    <div className="col">
                      <Card className="border-0 rounded-4">
                        <Card.Body>
                          <Card.Title>Attachments - {vehicle?.images?.length}</Card.Title>
                          {vehicle?.images?.length > 0 && (
                            <div className="row gap-2">
                              {vehicle?.images?.map((item, attachIndex) => (
                                <div style={{ width: "312px" }} key={attachIndex}>
                                  <div className="d-flex align-items-center bg-white rounded-4 p-2" style={{ border: "1px solid #EBEBEB" }}>
                                    <img src={attachment} alt="attachment" style={{ width: "24px", height: "24px" }} />
                                    <div
                                      style={{
                                        width: "calc(100% - 32px)",
                                        marginLeft: "8px",
                                      }}
                                    >
                                      <div
                                        className="fs-13 fw-500"
                                        style={{
                                          width: "100%",
                                          color: "#172B4D",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                        }}
                                      >
                                        {item.file_name ?? "Attachment"}
                                      </div>
                                      <div className="fs-12 fw-400" style={{ color: "#8790A1" }}>
                                        {item.file_size ?? "80.62 KB"}
                                      </div>
                                    </div>
                                    {item.url && (
                                      <div
                                        onClick={() => {
                                          downloadFile(item.url, item.url.split("/").pop());
                                        }}
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          borderRadius: "100px",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <img
                                          src={download}
                                          alt="download"
                                          style={{
                                            width: "12.8px",
                                            height: "12px",
                                            top: "10px",
                                            left: "9.6px",
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>

                    <div className="col-xl-12 ">
                      <Card className="border-0 rounded-4">
                        <Card.Body>
                          <Card.Title>Our Location</Card.Title>
                          <div style={{ height: "290px", width: "100%" }}>
                            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.128274081712!2d80.21165839999999!3d12.9636427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525d628d45f3a7%3A0xb699ddbfaf9c98be!2sLentera%20Technologies%20Pvt.%20Ltd.!5e0!3m2!1sen!2sin!4v1755339918233!5m2!1sen!2sin" width="100%" height="100%" allowFullScreen={true} title="location" loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
                          </div>
                        </Card.Body>
                      </Card>
                    </div>
                  </>
                )}

                {activeTab === "RC, Insurance & Permit details" && (
                  <VehicleDocumentsPage
                    vehicleId={location.state?.id}
                    licensePlate={vehicle?.lic_no}
                    handleClose={handleClose}
                    refreshKey={docRefreshKey}
                    isParivahan={isParivahan}
                    onUpdateSuccess={() => {
                      fetchVehicle();
                      setDocRefreshKey((prev) => prev + 1);
                    }}
                  />
                )}
                {activeTab === "Financial" && <FinancialView vehicleId={location.state?.id} handleClose={handleClose} />}
                {activeTab === "Specifications" && <Specifications vehicleId={location.state?.id} />}
                {activeTab === "Service history" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <ServiceHistoryTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Fuel history" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <FuelHistoryTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Inspection history" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <InspectionHistoryTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Issues" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <IssueHistoryTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "KM history" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <KMHistoryTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Service reminder" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <ServiceReminderTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Renewal reminder" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <RenewalHistoryTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Complaint" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <ComplaintsTableList vehicleId={location.state?.id} />
                  </div>
                )}
                {activeTab === "Assigned history" && (
                  <div className="bg-white w-100 rounded-4 mb-3">
                    <AssignedTableList vehicleId={location.state?.id} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer position="top-end">
        <Toast className="d-inline-block m-1" bg={snackbarStatus === true ? "success" : "danger"} show={showSnackbar} onClose={handleSnackbarClose} delay={3000} autohide>
          <Toast.Body style={{ color: "#ffffff" }}>{snackbarMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};
